// App.js — FABCON 2026 x Improving · Intent Signals Dashboard (single file)
// Drop into CRA / Vite / CodeSandbox. Then upload the CSV exported from your Colab
// harvester (fabcon_improving_interest.csv).

import React, { useMemo, useState, useEffect } from "react";

/* ============================ LocalStorage helpers ============================ */
const LS_ROWS = "FABCON_ROWS_V1";
const LS_FILTERS = "FABCON_FILTERS_V1";

function getLocal(k) {
  try {
    const v = localStorage.getItem(k);
    return v === null ? "" : v;
  } catch {
    return "";
  }
}
function setLocal(k, v) {
  try {
    localStorage.setItem(k, v ?? "");
  } catch {}
}

/* =============================== CSV utilities =============================== */

function readFile(e, onText) {
  const f = e.target.files?.[0];
  if (!f) return;
  const r = new FileReader();
  r.onload = (ev) => onText(String(ev.target.result || ""));
  r.readAsText(f);
}

// Very small CSV parser (no deps)
function parseCSV(text) {
  const lines = String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");
  if (!lines.length) return { headers: [], data: [] };
  const headers = splitCSVLine(lines[0]).map((h) => (h ?? "").trim());
  const data = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const fields = splitCSVLine(line);
    const row = {};
    for (let c = 0; c < headers.length; c++) row[headers[c]] = fields[c] ?? "";
    data.push(row);
  }
  return { headers, data };
}
function splitCSVLine(line) {
  const out = [];
  const re = /(\"([^\"]*(?:\"\"[^\"]*)*)\"|[^,]*)(,|$)/g;
  let m;
  while ((m = re.exec(line)) !== null) {
    const val = m[2] !== undefined ? m[2] : m[1];
    out.push((val ?? "").replace(/\"\"/g, '"'));
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  return out;
}

/* =============================== Data helpers =============================== */

function parseListish(s = "") {
  return String(s || "")
    .split("|")
    .map((x) => x.trim())
    .filter(Boolean);
}

function parseDateGuess(s = "") {
  if (!s) return null;
  const t = Date.parse(s);
  if (!Number.isNaN(t)) return new Date(t);
  // try basic yyyy-mm-dd from PostDate, or pick first 20xx in the string
  const m = String(s).match(/\b(20\d{2})[-/](\d{1,2})[-/](\d{1,2})\b/);
  if (m) {
    const [_, y, mo, d] = m;
    const dt = Date.parse(`${y}-${mo}-${d}`);
    if (!Number.isNaN(dt)) return new Date(dt);
  }
  return null;
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function inRecency(d, bucket) {
  if (!bucket) return true;
  if (!d) return false;
  const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (bucket === "7d") return days <= 7;
  if (bucket === "30d") return days <= 30;
  if (bucket === "90d") return days <= 90;
  return true;
}

function dateSpan(dates = []) {
  if (!dates.length) return "";
  const dmin = new Date(Math.min(...dates.map((d) => d.getTime())));
  const dmax = new Date(Math.max(...dates.map((d) => d.getTime())));
  const toISO = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  return `${toISO(dmin)} → ${toISO(dmax)}`;
}

function countBy(arr = [], fn = (x) => x) {
  const m = {};
  (arr || []).forEach((x) => {
    const k = fn(x) ?? "__";
    m[k] = (m[k] || 0) + 1;
  });
  return m;
}

function formatPlatform(pRaw = "") {
  const p = String(pRaw || "").trim();
  if (/linkedin\.com/i.test(p)) return "LinkedIn";
  if (/reddit\.com/i.test(p)) return "Reddit";
  return p || "Web";
}

/**
 * Normalize a FABCON harvester row into a dashboard record.
 * Expects columns like:
 *  Title, Snippet, Platform, Event, EventType, IntentTier,
 *  MatchedDreamforceTerms, DreamforceTermCount,
 *  MatchedStructuredSignals, StructuredSignalCount,
 *  PostDate, YearsFromSnippet, Linkedin Link, PostURL, link
 */
function normalizeRows(rawRows = []) {
  let id = 1;
  return (rawRows || [])
    .map((r) => {
      const title = String(
        r["Title"] || r["title"] || r["Page Title"] || ""
      ).trim();
      const snippet = String(r["Snippet"] || r["snippet"] || "").trim();

      const platformCol =
        r["Platform"] || r["platform"] || r["Source"] || r["source"] || "";
      const platform = formatPlatform(platformCol);

      const event = String(r["Event"] || "").trim();
      const eventType = String(r["EventType"] || "").trim();

      const intentTier = String(r["IntentTier"] || "").trim() || "General";

      const matchedFabcon = String(
        r["MatchedDreamforceTerms"] || r["MatchedEventTerms"] || ""
      ).trim();
      const dreamforceTermCount = Number(
        r["DreamforceTermCount"] || r["EventTermCount"] || 0
      );

      const matchedImproving = String(
        r["MatchedStructuredSignals"] || r["MatchedImprovingSignals"] || ""
      ).trim();
      const structuredSignalCount = Number(
        r["StructuredSignalCount"] || r["ImprovingSignalCount"] || 0
      );

      const postDateRaw = String(r["PostDate"] || "").trim();
      const postDateObj =
        parseDateGuess(postDateRaw) ||
        parseDateGuess(r["YearsFromSnippet"] || "");
      const postDate = postDateObj ? toISODate(postDateObj) : "";

      const yearsFromSnippet = String(r["YearsFromSnippet"] || "").trim();

      const link = String(
        r["Linkedin Link"] ||
          r["LinkedIn Link"] ||
          r["PostURL"] ||
          r["link"] ||
          r["Link"] ||
          ""
      ).trim();

      return {
        id: id++,
        title,
        snippet,
        platform,
        event,
        eventType,
        intentTier,
        matchedFabcon,
        dreamforceTermCount,
        matchedImproving,
        structuredSignalCount,
        postDate,
        postDateObj,
        yearsFromSnippet,
        link,
      };
    })
    .filter((r) => r.title || r.snippet || r.link);
}

/* =============================== Root App ================================= */

export default function App() {
  return (
    <div className="min-h-screen text-ink bg-bg">
      <Styles />
      <a href="#main" className="skip">
        Skip to content
      </a>
      <AppShell>
        <main id="main" className="container">
          <FabconInsights />
        </main>
      </AppShell>
    </div>
  );
}

/* ============================== Main Page ================================= */

function FabconInsights() {
  const [rawRows, setRawRows] = useState(() => {
    try {
      const raw = getLocal(LS_ROWS);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const savedFilters = useMemo(() => {
    try {
      const raw = getLocal(LS_FILTERS);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const [tier, setTier] = useState(savedFilters.tier ?? "");
  const [platform, setPlatform] = useState(savedFilters.platform ?? "");
  const [eventOnly, setEventOnly] = useState(savedFilters.eventOnly ?? false);
  const [recency, setRecency] = useState(savedFilters.recency ?? "");
  const [minFabconTerms, setMinFabconTerms] = useState(
    savedFilters.minFabconTerms ?? 0
  );
  const [minImprovingSignals, setMinImprovingSignals] = useState(
    savedFilters.minImprovingSignals ?? 0
  );
  const [q, setQ] = useState(savedFilters.q ?? "");

  useEffect(() => {
    try {
      setLocal(LS_ROWS, JSON.stringify(rawRows));
    } catch {}
  }, [rawRows]);

  useEffect(() => {
    const filters = {
      tier,
      platform,
      eventOnly,
      recency,
      minFabconTerms,
      minImprovingSignals,
      q,
    };
    try {
      setLocal(LS_FILTERS, JSON.stringify(filters));
    } catch {}
  }, [
    tier,
    platform,
    eventOnly,
    recency,
    minFabconTerms,
    minImprovingSignals,
    q,
  ]);

  const enriched = useMemo(() => normalizeRows(rawRows), [rawRows]);

  const platforms = useMemo(() => {
    const bag = new Set();
    enriched.forEach((r) => r.platform && bag.add(r.platform));
    return ["", ...Array.from(bag).sort()];
  }, [enriched]);

  const tiers = useMemo(() => {
    const bag = new Set();
    enriched.forEach((r) => r.intentTier && bag.add(r.intentTier));
    const sorted = Array.from(bag);
    // keep 🔥 first if present
    sorted.sort((a, b) => {
      const score = (t) =>
        t.startsWith("🔥")
          ? 4
          : t.startsWith("Hot")
          ? 3
          : t.startsWith("Warm")
          ? 2
          : t === "General"
          ? 1
          : 0;
      return score(b) - score(a);
    });
    return ["", ...sorted];
  }, [enriched]);

  const filtered = useMemo(() => {
    let list = enriched.slice();
    if (tier) list = list.filter((r) => r.intentTier === tier);
    if (platform) list = list.filter((r) => r.platform === platform);
    if (eventOnly) list = list.filter((r) => r.event);
    if (recency) list = list.filter((r) => inRecency(r.postDateObj, recency));
    if (minFabconTerms > 0)
      list = list.filter((r) => (r.dreamforceTermCount || 0) >= minFabconTerms);
    if (minImprovingSignals > 0)
      list = list.filter(
        (r) => (r.structuredSignalCount || 0) >= minImprovingSignals
      );
    if (q.trim()) {
      const QQ = q.trim().toLowerCase();
      list = list.filter(
        (r) =>
          (r.title || "").toLowerCase().includes(QQ) ||
          (r.snippet || "").toLowerCase().includes(QQ) ||
          (r.matchedFabcon || "").toLowerCase().includes(QQ) ||
          (r.matchedImproving || "").toLowerCase().includes(QQ)
      );
    }

    const rank = (t) =>
      t.startsWith("🔥")
        ? 4
        : t.startsWith("Hot")
        ? 3
        : t.startsWith("Warm")
        ? 2
        : t === "General"
        ? 1
        : 0;

    return list.sort((a, b) => {
      const r = rank(b.intentTier) - rank(a.intentTier);
      if (r) return r;
      const tb = b.postDateObj ? b.postDateObj.getTime() : 0;
      const ta = a.postDateObj ? a.postDateObj.getTime() : 0;
      if (tb !== ta) return tb - ta;
      if ((b.dreamforceTermCount || 0) !== (a.dreamforceTermCount || 0))
        return (b.dreamforceTermCount || 0) - (a.dreamforceTermCount || 0);
      return (b.structuredSignalCount || 0) - (a.structuredSignalCount || 0);
    });
  }, [
    enriched,
    tier,
    platform,
    eventOnly,
    recency,
    minFabconTerms,
    minImprovingSignals,
    q,
  ]);

  const stats = useMemo(() => {
    const byPlatform = countBy(filtered, (r) => r.platform || "Unknown");
    const byTier = countBy(filtered, (r) => r.intentTier || "General");
    const eventHits = filtered.filter((r) => r.event).length;
    return {
      rows: filtered.length,
      byPlatform,
      extraHot:
        byTier["🔥 Extra-Hot (FABCON × Improving)"] ||
        byTier["🔥 Extra-Hot (DF × Structured)"] ||
        0,
      hot: byTier["Hot (FABCON / Fabric)"] || byTier["Hot (Dreamforce)"] || 0,
      warm: byTier["Warm (Improving-fit)"] || byTier["Warm (Structured)"] || 0,
      general: byTier["General"] || 0,
      eventHits,
      period: dateSpan(filtered.map((r) => r.postDateObj).filter(Boolean)),
    };
  }, [filtered]);

  const exportCurrent = () => {
    const header = [
      "Row",
      "IntentTier",
      "FabconTermCount",
      "ImprovingSignalCount",
      "MatchedFabconTerms",
      "MatchedImprovingSignals",
      "Title",
      "Snippet",
      "Platform",
      "Event",
      "EventType",
      "PostDate",
      "YearsFromSnippet",
      "Link",
    ];
    const matrix = filtered.map((r, i) => [
      String(i + 1),
      r.intentTier || "",
      String(r.dreamforceTermCount ?? 0),
      String(r.structuredSignalCount ?? 0),
      r.matchedFabcon || "",
      r.matchedImproving || "",
      r.title || "",
      r.snippet || "",
      r.platform || "",
      r.event || "",
      r.eventType || "",
      r.postDate || "",
      r.yearsFromSnippet || "",
      r.link || "",
    ]);
    const csv = arrayToCsv([header, ...matrix]);
    downloadText(
      csv,
      `fabcon_signals_view_${dateStamp()}.csv`,
      "text/csv;charset=utf-8"
    );
  };

  const clearPersisted = () => {
    if (!window.confirm("Clear saved rows & filters?")) return;
    setRawRows([]);
    try {
      setLocal(LS_ROWS, "");
      setLocal(LS_FILTERS, "");
    } catch {}
  };

  return (
    <>
      <PageHeader
        title="FABCON 2026 · Intent Signals"
        description="Upload the FABCON x Improving CSE CSV to explore where Microsoft Fabric, event language, and Improving-style solution topics overlap across LinkedIn and Reddit."
        primaryAction={
          <Button onClick={exportCurrent} aria-label="Export filtered CSV">
            Export CSV
          </Button>
        }
        secondaryAction={
          rawRows?.length ? (
            <Button variant="secondary" onClick={clearPersisted}>
              Clear saved view
            </Button>
          ) : null
        }
      />

      {/* Upload */}
      <Card>
        <h3 className="h3 mb-2">Upload FABCON Signals CSV</h3>
        <label className="uploader">
          <div className="label">CSV</div>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              readFile(e, (t) => {
                const parsed = parseCSV(t).data;
                setRawRows(parsed);
              });
            }}
          />
          <div className="hint">
            Expected columns include: <code>Title</code>, <code>Snippet</code>,{" "}
            <code>Platform</code>, <code>Event</code>, <code>EventType</code>,{" "}
            <code>IntentTier</code>, <code>MatchedDreamforceTerms</code>,{" "}
            <code>MatchedStructuredSignals</code>,{" "}
            <code>DreamforceTermCount</code>, <code>StructuredSignalCount</code>
            , <code>PostDate</code>, <code>YearsFromSnippet</code>,{" "}
            <code>Linkedin Link</code>/<code>link</code>.
          </div>
        </label>
        <div className="flex wrap mt-2 gap-2">
          {rawRows?.length ? (
            <div className="ok">
              Loaded {enriched.length} rows from CSV. Your last session will
              auto-restore.
            </div>
          ) : (
            <div className="text-muted text-sm">
              Upload the Colab CSV to get started. The view and filters will be
              saved to your browser.
            </div>
          )}
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid md:grid-2 gap-3 px-1">
        <KPI label="Rows (filtered)" value={stats.rows} />
        <KPI label="Date span (filtered)" value={stats.period || "—"} />
      </div>
      <div className="grid md:grid-2 gap-3 px-1">
        <KPI label="🔥 Extra-Hot (FABCON × Improving)" value={stats.extraHot} />
        <KPI label="Hot (FABCON / Fabric)" value={stats.hot} />
        <KPI label="Warm (Improving-fit only)" value={stats.warm} />
        <KPI label="General" value={stats.general} />
        <KPI label="Event-tagged signals" value={stats.eventHits} />
      </div>

      {/* Filters */}
      <Card>
        <Toolbar>
          <Sel
            label="Intent Tier"
            value={tier}
            onChange={setTier}
            options={tiers}
          />
          <Sel
            label="Platform"
            value={platform}
            onChange={setPlatform}
            options={platforms}
          />
          <Sel
            label="Recency"
            value={recency}
            onChange={setRecency}
            options={["", "7d", "30d", "90d"]}
          />
          <NumberSel
            label="Min FABCON terms"
            value={minFabconTerms}
            setValue={setMinFabconTerms}
            steps={[0, 1, 2, 3, 5]}
          />
          <NumberSel
            label="Min Improving signals"
            value={minImprovingSignals}
            setValue={setMinImprovingSignals}
            steps={[0, 1, 2, 3, 5]}
          />
          <label className="toggle">
            <input
              type="checkbox"
              checked={eventOnly}
              onChange={(e) => setEventOnly(e.target.checked)}
            />
            <span className="toggle-label">Event-tagged only</span>
          </label>
          <div className="ml-auto flex gap-2">
            <input
              className="input"
              placeholder="Search title/snippet/signals…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ minWidth: 260 }}
              aria-label="Search"
            />
          </div>
        </Toolbar>
        {stats.period ? (
          <div className="text-muted text-xs mt-2">
            Date span (filtered): {stats.period}
          </div>
        ) : null}
      </Card>

      {/* Results table */}
      {filtered.length > 0 ? (
        <Card>
          <div className="section-head">
            <h3 className="h3">Signals (prioritized)</h3>
            <span className="text-muted text-xs">
              Sorted by intent tier, recency, and FABCON/Improving signal depth.
            </span>
          </div>
          <div className="table-wrap">
            <table className="tbl" role="table" aria-label="FABCON signals">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>#</th>
                  <th>Intent</th>
                  <th>FABCON / Fabric</th>
                  <th>Improving-fit</th>
                  <th>Title & Snippet</th>
                  <th>Meta</th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 250).map((r, i) => (
                  <tr key={r.id}>
                    <td className="text-muted">{i + 1}</td>
                    <td>
                      <strong>{r.intentTier || "General"}</strong>
                    </td>
                    <td>
                      <div className="text-sm">
                        <strong>Terms:</strong> {r.dreamforceTermCount ?? 0}
                      </div>
                      <div className="text-xs text-muted">
                        {r.matchedFabcon || "—"}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm">
                        <strong>Signals:</strong> {r.structuredSignalCount ?? 0}
                      </div>
                      <div className="text-xs text-muted">
                        {r.matchedImproving || "—"}
                      </div>
                    </td>
                    <td>
                      <div className="title">{r.title || "—"}</div>
                      <div className="snippet text-sm">{r.snippet || "—"}</div>
                    </td>
                    <td>
                      <div className="pill">{r.platform || "—"}</div>
                      <div className="text-xs text-muted mt-1">
                        {r.event || "No event tag"}
                      </div>
                      <div className="text-xs text-muted mt-1">
                        {r.eventType || ""}
                      </div>
                      <div className="text-xs text-muted mt-1">
                        {r.postDate || r.yearsFromSnippet || ""}
                      </div>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {r.link ? (
                        <a
                          className="btn secondary link-btn"
                          href={r.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open ↗
                        </a>
                      ) : (
                        <span className="text-muted text-xs">n/a</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-muted text-sm mt-2">
            Showing {Math.min(filtered.length, 250)} of {filtered.length} rows.
          </div>
        </Card>
      ) : (
        <EmptyState
          title="No signals yet"
          description="Upload the FABCON CSV and/or relax filters to see results."
        />
      )}
    </>
  );
}

/* ================================ UI shell ================================= */

function AppShell({ children }) {
  return (
    <>
      <header className="appbar" role="banner">
        <div className="container appbar-inner">
          <div className="brand">
            <img
              src="https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/logos/Final%20Logo_400.png"
              alt="Fairway logo"
              className="brand-logo"
            />
            <div className="brand-meta">
              <span className="brand-name">Fairway Signals</span>
              <span className="brand-sub">FABCON 2026 · Microsoft Fabric</span>
            </div>
          </div>
        </div>
      </header>
      {children}
      <footer className="footer container" role="contentinfo">
        <span className="text-muted text-xs">
          © {new Date().getFullYear()} Fairway Digital Media • FABCON 2026
          Intent Signals.
        </span>
      </footer>
    </>
  );
}

function PageHeader({ title, description, primaryAction, secondaryAction }) {
  return (
    <header className="pagehead">
      <div>
        <h1 className="h1">{title}</h1>
        {description ? <p className="sub">{description}</p> : null}
      </div>
      <div className="actions">
        {secondaryAction}
        {primaryAction}
      </div>
    </header>
  );
}

function Toolbar({ children }) {
  return <div className="toolbar">{children}</div>;
}

function KPI({ label, value }) {
  return (
    <div className="kpi">
      <div className="kpi-value">{String(value ?? "—")}</div>
      <div className="kpi-label">{label}</div>
    </div>
  );
}

function Card({ children }) {
  return <div className="card">{children}</div>;
}

function EmptyState({ title, description }) {
  return (
    <div className="empty">
      <h3 className="h3">{title}</h3>
      <p className="text-muted">{description}</p>
    </div>
  );
}

function Button({ variant = "primary", children, ...props }) {
  return (
    <button
      {...props}
      className={`btn ${variant}`}
      onMouseDown={(e) => e.currentTarget.classList.add("active")}
      onMouseUp={(e) => e.currentTarget.classList.remove("active")}
      onMouseLeave={(e) => e.currentTarget.classList.remove("active")}
    >
      {children}
    </button>
  );
}

function Sel({ label, value, onChange, options }) {
  return (
    <div className="select-wrap">
      <span className="sel-label">{label}:</span>
      <select
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      >
        {options.map((opt) => (
          <option key={opt || "(All)"} value={opt}>
            {opt || "(All)"}
          </option>
        ))}
      </select>
    </div>
  );
}

function NumberSel({ label, value, setValue, steps }) {
  return (
    <div className="select-wrap">
      <span className="sel-label">{label}:</span>
      <select
        className="input"
        value={String(value)}
        onChange={(e) => setValue(Number(e.target.value))}
        aria-label={label}
      >
        {steps.map((n) => (
          <option key={n} value={String(n)}>
            {n}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ================================ Styles ================================== */

function Styles() {
  const css = `
  :root{
    --ink:#0B1A3C; --muted:#6B7280;
    --line:#E5E7EB; --bg:#F7F8FA; --card:#FFFFFF;
    --brand:#22C55E; --brand10:#E8F8EF;
    --radius-sm:8px; --radius-md:12px; --radius-lg:16px;
    --shadow-xs:0 1px 2px rgba(0,0,0,.06);
    --shadow-sm:0 2px 4px rgba(0,0,0,.08);
    --shadow-md:0 4px 14px rgba(0,0,0,.08);
  }
  *{box-sizing:border-box}
  html,body,#root{min-height:100%;height:auto}
  body{margin:0;background:var(--bg);color:var(--ink);font:14px/1.45 Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto}

  .h1{font-size:28px;line-height:34px;font-weight:700;color:var(--ink)}
  .h3{font-size:22px;line-height:28px;font-weight:700;color:var(--ink)}
  .h4{font-size:16px;line-height:22px;font-weight:600;color:var(--ink)}
  .sub{color:var(--muted);font-size:13px;margin-top:4px}
  .text-ink{color:var(--ink)} .text-muted{color:var(--muted)} .text-xs{font-size:12px} .text-sm{font-size:13px}
  .mb-2{margin-bottom:8px} .mt-2{margin-top:8px} .mt-3{margin-top:12px}
  .px-1{padding-left:4px;padding-right:4px}

  .container{max-width:80rem;margin:0 auto;padding:0 24px}
  .grid{display:grid} .gap-3{gap:12px}
  .md\\:grid-2{grid-template-columns:1fr}
  @media (min-width:820px){.md\\:grid-2{grid-template-columns:repeat(2,1fr)}}
  .flex{display:flex} .wrap{flex-wrap:wrap} .gap-2{gap:8px}
  .ml-auto{margin-left:auto}

  .appbar{position:sticky;top:0;z-index:50;background:rgba(255,255,255,.9);backdrop-filter:saturate(180%) blur(8px);border-bottom:1px solid var(--line)}
  .appbar-inner{display:flex;align-items:center;gap:16px;padding:10px 0}
  .brand{display:flex;align-items:center;gap:10px}
  .brand-logo{height:28px;width:auto;border-radius:6px}
  .brand-meta{display:flex;flex-direction:column;line-height:1}
  .brand-name{font-weight:700;color:var(--ink)}
  .brand-sub{font-size:12px;color:var(--muted)}

  .pagehead{display:flex;align-items:start;justify-content:space-between;gap:16px;border-bottom:1px solid var(--line);padding:16px 0;margin:12px 0}
  .actions{display:flex;gap:8px;align-items:center}

  .card{background:var(--card);border:1px solid var(--line);border-radius:16px;padding:16px;margin:12px 0;box-shadow:var(--shadow-sm);transition:box-shadow .15s ease-out}
  .card:hover{box-shadow:var(--shadow-md)}

  .kpi{background:#fff;border:1px solid var(--line);border-radius:12px;padding:12px;margin:12px 0}
  .kpi-value{font-size:22px;font-weight:700;color:var(--ink)}
  .kpi-label{color:var(--muted);font-size:12px}

  .ok{background:var(--brand10);border:1px solid #cde8c6;border-radius:10px;padding:8px 10px;color:#234c1b}

  .toolbar{display:flex;flex-wrap:wrap;align-items:center;gap:10px}
  .select-wrap{display:flex;gap:8px;align-items:center}
  .sel-label{color:var(--muted);font-size:12px}
  .input, select, textarea{background:#fff;border:1px solid var(--line);color:var(--ink);border-radius:8px;padding:8px;min-width:140px;outline:none}
  .input:focus, select:focus, textarea:focus{box-shadow:0 0 0 3px rgba(34,197,94,.25);border-color:#86efac}

  .uploader input[type=file]{display:block;background:#fff;border:1px solid var(--line);border-radius:8px;padding:8px;width:100%}
  .uploader .label{font-weight:600;margin-bottom:6px}
  .uploader .hint{color:var(--muted);font-size:12px;margin-top:6px}

  .btn{background:linear-gradient(180deg, var(--brand) 0%, #16a34a 100%);border:1px solid #16a34a;color:#fff;border-radius:10px;padding:8px 12px;cursor:pointer;display:inline-flex;align-items:center;gap:8px;text-decoration:none;transition:transform .1s ease-out, box-shadow .15s ease-out}
  .btn:hover{box-shadow:var(--shadow-sm)}
  .btn.active{transform:translateY(1px)}
  .btn.secondary{background:#ffffff;color:#0B1A3C;border:1px solid var(--line)}
  .btn.secondary:hover{box-shadow:var(--shadow-xs)}
  .btn:focus-visible{outline:none;box-shadow:0 0 0 3px rgba(34,197,94,.25)}

  .pill{display:inline-block;padding:2px 8px;border-radius:999px;font-size:12px;border:1px solid var(--line);background:#fff}

  .table-wrap{max-height:70vh; overflow:auto; border:1px solid var(--line);border-radius:12px}
  .tbl{width:100%;border-collapse:separate;border-spacing:0}
  .tbl thead th{position:sticky;top:0;background:#F3F6FA;border-bottom:1px solid var(--line);text-align:left;padding:10px;font-size:12px;color:#475569;z-index:1}
  .tbl tbody tr:nth-child(odd){background:#fcfcfd}
  .tbl td{padding:10px 12px;border-bottom:1px solid #eef2f6;vertical-align:top}
  .title{font-weight:600}
  .snippet{white-space:pre-wrap}

  .footer{padding:24px 0;margin-top:24px;border-top:1px solid var(--line)}

  .empty{text-align:center;padding:40px 12px}
  .empty .h3{margin-bottom:8px}

  .skip{position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden}
  .skip:focus{left:16px;top:12px;width:auto;height:auto;background:#fff;border:2px solid var(--brand);padding:6px 10px;border-radius:8px;z-index:1000}

  @media (prefers-color-scheme: dark){
    :root{--bg:#020617; --card:#020617; --ink:#e5e7eb; --muted:#9aa3b2; --line:#1f2937}
    .tbl thead th{background:#020617;color:#cbd5e1}
    .tbl tbody tr:nth-child(odd){background:#020617}
    .card{background:#020617}
    .pagehead{border-color:#1f2937}
    .appbar{background:rgba(15,23,42,.9)}
    .btn.secondary{background:transparent;color:#e5e7eb}
  }
  `;
  return <style>{css}</style>;
}

/* ========================== Small download helpers ========================= */

function arrayToCsv(matrix) {
  const BOM = "\uFEFF";
  const lines = matrix.map((row) => row.map(csvEscape).join(","));
  return BOM + lines.join("\r\n");
}
function csvEscape(val) {
  const s = String(val ?? "");
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadText(text, filename, mime = "text/plain;charset=utf-8") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
function dateStamp() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}_${String(d.getHours()).padStart(
    2,
    "0"
  )}${String(d.getMinutes()).padStart(2, "0")}`;
}
